import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Typography, Button, Alert, AlertTitle } from '@mui/material';
import { ERROR_MESSAGES } from '../../config/constants';

// ✅ INTERFACE PARA EL ESTADO DEL ERROR BOUNDARY
interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

// ✅ INTERFACE PARA LAS PROPS
interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

// ✅ COMPONENTE DE FALLBACK POR DEFECTO
const DefaultErrorFallback: React.FC<{
  error: Error;
  resetError: () => void;
}> = ({ error, resetError }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      padding: 3,
      textAlign: 'center',
    }}
  >
    <Alert
      severity="error"
      sx={{
        maxWidth: 600,
        width: '100%',
        marginBottom: 3,
      }}
    >
      <AlertTitle>Algo salió mal</AlertTitle>
      <Typography variant="body1" sx={{ marginBottom: 2 }}>
        {ERROR_MESSAGES.UNKNOWN_ERROR}
      </Typography>
      {process.env.NODE_ENV === 'development' && (
        <Box sx={{ textAlign: 'left', marginTop: 2 }}>
          <Typography variant="h6" sx={{ marginBottom: 1 }}>
            Detalles del error (solo en desarrollo):
          </Typography>
          <Typography
            variant="body2"
            component="pre"
            sx={{
              backgroundColor: 'grey.100',
              padding: 1,
              borderRadius: 1,
              overflow: 'auto',
              fontSize: '0.75rem',
            }}
          >
            {error.stack}
          </Typography>
        </Box>
      )}
    </Alert>
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
      <Button variant="contained" onClick={resetError} color="primary">
        Intentar nuevamente
      </Button>
      <Button
        variant="outlined"
        onClick={() => window.location.reload()}
        color="secondary"
      >
        Recargar página
      </Button>
    </Box>
  </Box>
);

// ✅ ERROR BOUNDARY PRINCIPAL
export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  // ✅ CAPTURAR ERRORES
  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  // ✅ MANEJAR ERRORES
  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({
      error,
      errorInfo,
    });

    // ✅ LOGGING DEL ERROR
    console.error('Error Boundary caught an error:', error, errorInfo);

    // ✅ CALLBACK PERSONALIZADO
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // ✅ ENVIAR A SERVICIO DE MONITOREO (OPCIONAL)
    this.logErrorToService(error, errorInfo);
  }

  // ✅ LOGGING A SERVICIO EXTERNO
  private logErrorToService = (error: Error, errorInfo: ErrorInfo) => {
    try {
      // ✅ AQUÍ SE PUEDE INTEGRAR CON SERVICIOS COMO:
      // - Sentry
      // - LogRocket
      // - Bugsnag
      // - Rollbar

      if (process.env.REACT_APP_ERROR_LOGGING_URL) {
        fetch(process.env.REACT_APP_ERROR_LOGGING_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            error: {
              message: error.message,
              stack: error.stack,
              name: error.name,
            },
            errorInfo: {
              componentStack: errorInfo.componentStack,
            },
            timestamp: new Date().toISOString(),
            userAgent: navigator.userAgent,
            url: window.location.href,
          }),
        }).catch(console.error);
      }
    } catch (loggingError) {
      console.error('Error logging to service:', loggingError);
    }
  };

  // ✅ RESET DEL ERROR
  resetError = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      // ✅ RENDERIZAR FALLBACK PERSONALIZADO O POR DEFECTO
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // ✅ VERIFICACIÓN DE TIPO SEGURA
      if (!this.state.error) {
        return null; // Fallback si no hay error
      }

      return (
        <DefaultErrorFallback
          error={this.state.error}
          resetError={this.resetError}
        />
      );
    }

    return this.props.children;
  }
}

// ✅ HOOK PARA USAR ERROR BOUNDARY EN COMPONENTES FUNCIONALES
export const useErrorHandler = () => {
  const handleError = (error: Error, errorInfo?: any) => {
    console.error('Error caught by useErrorHandler:', error, errorInfo);

    // ✅ AQUÍ SE PUEDE IMPLEMENTAR LÓGICA ADICIONAL
    // - Mostrar notificación al usuario
    // - Enviar a servicio de monitoreo
    // - Logging local

    throw error; // Re-lanzar para que el Error Boundary lo capture
  };

  return { handleError };
};

export default ErrorBoundary;
